import { CSSProperties, ReactNode, SyntheticEvent } from 'react';
import {
  SxProps,
  TableCell as MUITableCell,
  TableCellProps as MUITableCellProps,
  TableSortLabel as MUITableSortLabel,
} from '@mui/material';
import * as R from 'ramda';

import { Icon } from '@atom/mui';
import { ArrowDownwardIcon, ArrowUpwardIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

type CustomProps = {
  /**
   * Hard-codes the width of the table cell such that it will not shrink
   * or grow based on content.
   */
  width?: string | number;
  /**
   * Centers the content in the cell
   */
  center?: boolean;
  /**
   * Optional callback to be called when sort direction changes.
   */
  onSortChange?: (sort: 'asc' | 'desc') => void;
  /**
   * Optional CSS styles to be applied to the cell.
   */
  style?: CSSProperties;
  /**
   * Displays the cell elements stacked and aligned to the left
   * - This is overridden if center prop is passed or align === 'right'
   */
  columnDisplay?: boolean;
};

type AtomMUIProps = {
  children?: ReactNode;
  className?: string;
  'data-cy'?: string;
  sortDirection?: 'asc' | 'desc' | false;
  align?: string;
  variant?: 'head' | 'body' | 'footer';
  padding?: 'none' | 'normal' | 'checkbox';
  onClick?: (event: SyntheticEvent<any>) => void;
  colSpan?: number;
};

type TableCellProps = CustomProps & AtomMUIProps;

const styles = {
  head: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headCenter: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headRight: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
    alignItems: 'center',
  },
  columnDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  icon: {
    fontSize: '1rem',
    marginLeft: '0.5rem',
  },
};

const getPadding = align =>
  align === 'right' ? '0.5rem 1.5rem 0.5rem 0' : '0.5rem 0 0.5rem 1.5rem';

const TableCell = (props: TableCellProps) => {
  const {
    variant,
    children,
    sortDirection,
    onSortChange,
    center,
    align,
    style,
    width,
    columnDisplay,
    ...data
  } = props;

  const MUIProps: MUITableCellProps = data;

  const handleCellClick = () => {
    if (onSortChange) {
      onSortChange(sortDirection === 'asc' ? 'desc' : 'asc');
    }
  };

  const minWidth =
    !width &&
    typeof children === 'string' &&
    `calc(${children.length + 2}ch + ${
      !R.isNil(sortDirection) ? '1.5rem' : '0px'
    })`;

  const sx: SxProps = {
    padding: getPadding(align),
    minWidth: width,
    maxWidth: width,
    width,
    textAlign: center ? 'center' : 'left',
    ...style,
    '&.MuiTableCell-head': {
      background: colors.neutral.fog,
      color: !!sortDirection ? colors.neutral.dark : colors.neutral.gray,
      fontSize: fonts.sm,
      textTransform: 'uppercase',
      cursor: !R.isNil(sortDirection) ? 'pointer' : 'inherit',
      ...(minWidth && { minWidth }),
    },
  };

  return (
    <MUITableCell
      padding="none"
      variant={variant}
      sortDirection={sortDirection}
      onClick={handleCellClick}
      sx={sx}
      {...MUIProps}
    >
      <div
        style={
          align === 'right'
            ? styles.headRight
            : center
              ? styles.headCenter
              : columnDisplay
                ? styles.columnDisplay
                : styles.head
        }
      >
        {children}
        {!R.isNil(sortDirection) && (
          <MUITableSortLabel
            active
            direction={sortDirection || 'desc'}
            IconComponent={() => (
              <Icon
                style={styles.icon}
                color={
                  !!sortDirection ? colors.neutral.black : colors.neutral.gray
                }
                icon={
                  sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon
                }
              />
            )}
          />
        )}
      </div>
    </MUITableCell>
  );
};

export default TableCell;
